/* ──────────────────────────────────────────────────────────────────────
 *  Shared dimension configuration — icons, colors, and keys
 *
 *  Extracted so any component (panel headers, badges, tooltips) can
 *  render dimension icons and colors without importing the full
 *  DimensionRadar component.
 * ────────────────────────────────────────────────────────────────────── */

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Brain, Volume2, Heart, User, Bird, TreeDeciduous } from 'lucide-react';

export type DimKey = 'mind' | 'body' | 'heart' | 'wings' | 'roots' | 'voice';

export interface DimConfig {
  key: DimKey;
  /**
   * Lucide icon component.
   * DEV-963: typed as LucideIcon (the canonical lucide-react export type)
   * rather than `React.FC<{ color?: string; size?: number; ...}>`. The FC
   * shape doesn't match Lucide's actual icon type, which is a
   * ForwardRefExoticComponent. LucideIcon is exported by lucide-react
   * specifically for this use case and accepts all the props the icons
   * actually take, including the ones we use (color, size, strokeWidth).
   */
  icon: LucideIcon;
  /** CSS custom property with fallback hex. */
  color: string;
}

/**
 * Canonical dimension configuration.
 * Order follows the radar layout: Mind → Voice → Heart → Body → Wings → Roots.
 */
export const DIMENSION_CONFIG: Record<DimKey, DimConfig> = {
  mind:  { key: 'mind',  icon: Brain,          color: 'var(--dim-mind, #7ea3cc)' },
  body:  { key: 'body',  icon: User,           color: 'var(--dim-body, #6aab7a)' },
  heart: { key: 'heart', icon: Heart,          color: 'var(--dim-heart, #c77a6f)' },
  wings: { key: 'wings', icon: Bird,            color: 'var(--dim-wings, #c9a24e)' },
  roots: { key: 'roots', icon: TreeDeciduous,  color: 'var(--dim-roots, #8B7355)' },
  voice: { key: 'voice', icon: Volume2,        color: 'var(--dim-voice, #5aabb5)' },
};

/**
 * Render a row of dimension icons for a panel header.
 * Pass the dimension keys that the panel primarily grows.
 */
export function DimensionHintIcons({
  dimensions,
  size = 20,
  strokeWidth = 2.2,
  className,
}: {
  dimensions: DimKey[];
  size?: number;
  strokeWidth?: number;
  className?: string;
}) {
  return React.createElement(
    'span',
    {
      className,
      style: { display: 'inline-flex', alignItems: 'center', gap: '10px' },
      'aria-hidden': 'true',
    },
    ...dimensions.map((dimKey) => {
      const cfg = DIMENSION_CONFIG[dimKey];
      if (!cfg) return null;
      return React.createElement(cfg.icon, {
        key: dimKey,
        color: cfg.color,
        size,
        strokeWidth,
      });
    }),
  );
}
